import { useEffect, useRef } from 'react';
import { UPLOAD_SCENE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { ReferencePrompt, VideoPrompt } from '@/app/types';
import { uploadFile } from '@/app/utils/client';

type TempVideoPrompt = VideoPrompt & {
  _tempFile?: File;
  _tempCoverImage?: Blob;
};

export const useReferenceTempVideosUpload = (
  referencePrompts: ReferencePrompt[],
  updateReferenceAndMentions: (id: string, updates: Partial<ReferencePrompt>) => void,
) => {
  const userInfo = useStore((state) => state.userInfo);
  const processedUploadKeysRef = useRef<Set<string>>(new Set());
  const pendingUploadKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!userInfo) {
      return;
    }

    const pendingUploads = referencePrompts
      .filter(
        (ref): ref is ReferencePrompt & { type: 'video'; prompt: VideoPrompt; status: 'done' } =>
          ref.type === 'video' && ref.prompt !== null && ref.status === 'done',
      )
      .map((ref) => {
        const prompt = ref.prompt as TempVideoPrompt;
        if (!prompt._tempFile) {
          return null;
        }

        const uploadKey = `reference-temp-video:${ref.id}`;
        if (processedUploadKeysRef.current.has(uploadKey) || pendingUploadKeysRef.current.has(uploadKey)) {
          return null;
        }

        return { ref, uploadKey };
      })
      .filter(
        (
          item,
        ): item is {
          ref: ReferencePrompt & { type: 'video'; prompt: VideoPrompt; status: 'done' };
          uploadKey: string;
        } => item !== null,
      );

    if (pendingUploads.length === 0) {
      return;
    }

    const uploadPromises = pendingUploads.map(async ({ ref, uploadKey }) => {
      pendingUploadKeysRef.current.add(uploadKey);
      updateReferenceAndMentions(ref.id, { status: 'uploading' });

      try {
        const currentPrompt = ref.prompt as TempVideoPrompt;
        if (!currentPrompt._tempFile) {
          updateReferenceAndMentions(ref.id, { status: 'done' });
          return;
        }

        const [videoUploadResult, coverUploadResult] = await Promise.all([
          uploadFile({
            file: currentPrompt._tempFile,
            width: currentPrompt.width ?? 0,
            height: currentPrompt.height ?? 0,
            duration: currentPrompt.duration,
            scene: UPLOAD_SCENE.VIDU,
          }),
          currentPrompt._tempCoverImage
            ? uploadFile({
                file: currentPrompt._tempCoverImage,
                scene: UPLOAD_SCENE.VIDU,
              })
            : Promise.resolve(null),
        ]);

        if (videoUploadResult.error || !videoUploadResult.uri) {
          console.error('Failed to upload temp reference video:', ref.id, videoUploadResult.error);
          updateReferenceAndMentions(ref.id, { status: 'done' });
          return;
        }

        const { _tempFile, _tempCoverImage, ...promptWithoutTemp } = currentPrompt;
        updateReferenceAndMentions(ref.id, {
          status: 'done',
          prompt: {
            ...promptWithoutTemp,
            videoId: videoUploadResult.id ?? promptWithoutTemp.videoId,
            video: videoUploadResult.uri,
            videoPreview: promptWithoutTemp.videoPreview || videoUploadResult.uri,
            coverImage:
              !coverUploadResult || coverUploadResult.error || !coverUploadResult.uri
                ? promptWithoutTemp.coverImage
                : coverUploadResult.uri,
            coverImagePreview:
              promptWithoutTemp.coverImagePreview || promptWithoutTemp.coverImage || promptWithoutTemp.videoPreview,
          },
        });
        processedUploadKeysRef.current.add(uploadKey);
      } finally {
        pendingUploadKeysRef.current.delete(uploadKey);
      }
    });

    Promise.all(uploadPromises).catch((error) => {
      console.error('Failed to upload temp reference videos:', error);
    });
  }, [userInfo, referencePrompts, updateReferenceAndMentions]);
};
